import {
  PaperworkSectionRoute,
  ProviderGroupPaperworkSectionRoute,
  ProviderGroupPaperworkFormName,
} from '../../paperworkConstants';
import { isPhotoIdUploadEnabledForEntrypoint } from '../../../../core/util/location';
import { isPhotoIdUploadedForBooking } from '../../../../core/util/booking';
import { isPhotoIdUploadedForUserProfile } from '~/core/util/account';
import { isConsentIncluded } from '../../util';
import { SectionInfo, SectionPage, SectionPageCollection } from '../../interfaces';
import { PaperworkState } from '~/ducks/paperwork';
import {
  PATIENT_PAGES,
  MEDICAL_PAGES,
  LIFESTYLE_FACTORS_PAGES,
  HEALTHCARE_CONTACTS_PAGES,
  VERIFICATION_AND_REVIEW_PAGES,
} from '../constants';
import { pageHasAvailableFields } from '../../util/page';

export enum PGSectionNumber {
  Start,
  Patient,
  Medical,
  LifestyleFactors,
  HealthcareContacts,
  PhotoId,
  VerificationAndReview,
  Consent,
  Finalize,
}

type SectionsInfo = Map<PGSectionNumber, SectionInfo>;

const sectionHasAvailableFields = (
  sectionPages: SectionPageCollection,
  paperwork: PaperworkState
) => {
  return [...sectionPages.values()].some((page: SectionPage) =>
    pageHasAvailableFields(paperwork, true, page)
  );
};

export const PG_SECTIONS: SectionsInfo = new Map([
  [
    PGSectionNumber.Start,
    {
      getRoute: (bookingId) => `${PaperworkSectionRoute.START}/${bookingId}`,
      startingProgress: 0,
    },
  ],
  [
    PGSectionNumber.Patient,
    {
      getRoute: (bookingId) => `${ProviderGroupPaperworkSectionRoute.PATIENT}/${bookingId}`,
      form: ProviderGroupPaperworkFormName.PATIENT,
      pages: PATIENT_PAGES,
      dependencyFn: ({ paperwork }) => sectionHasAvailableFields(PATIENT_PAGES, paperwork),
      startingProgress: 10,
    },
  ],
  [
    PGSectionNumber.Medical,
    {
      getRoute: (bookingId) => `${ProviderGroupPaperworkSectionRoute.MEDICAL}/${bookingId}`,
      form: ProviderGroupPaperworkFormName.MEDICAL,
      pages: MEDICAL_PAGES,
      dependencyFn: ({ paperwork }) => sectionHasAvailableFields(MEDICAL_PAGES, paperwork),
      startingProgress: 30,
    },
  ],
  [
    PGSectionNumber.LifestyleFactors,
    {
      getRoute: (bookingId) =>
        `${ProviderGroupPaperworkSectionRoute.LIFESTYLE_FACTORS}/${bookingId}`,
      form: ProviderGroupPaperworkFormName.LIFESTYLE_FACTORS,
      pages: LIFESTYLE_FACTORS_PAGES,
      dependencyFn: ({ paperwork }) =>
        sectionHasAvailableFields(LIFESTYLE_FACTORS_PAGES, paperwork),
      startingProgress: 50,
    },
  ],
  [
    PGSectionNumber.HealthcareContacts,
    {
      getRoute: (bookingId) =>
        `${ProviderGroupPaperworkSectionRoute.HEALTHCARE_CONTACTS}/${bookingId}`,
      form: ProviderGroupPaperworkFormName.HEALTHCARE_CONTACTS,
      pages: HEALTHCARE_CONTACTS_PAGES,
      dependencyFn: ({ paperwork }) =>
        sectionHasAvailableFields(HEALTHCARE_CONTACTS_PAGES, paperwork),
      startingProgress: 65,
    },
  ],
  [
    PGSectionNumber.PhotoId,
    {
      getRoute: (bookingId) => `${ProviderGroupPaperworkSectionRoute.PHOTO_ID}?bid=${bookingId}`,
      dependencyFn: ({ location, booking, userProfile }) => {
        return (
          isPhotoIdUploadEnabledForEntrypoint(location as any, 'paperwork') &&
          !isPhotoIdUploadedForBooking(booking) &&
          (!userProfile || !isPhotoIdUploadedForUserProfile(userProfile))
        );
      },
      startingProgress: 75,
    },
  ],
  [
    PGSectionNumber.VerificationAndReview,
    {
      getRoute: (bookingId) =>
        `${ProviderGroupPaperworkSectionRoute.VERIFICATION_AND_REVIEW}/${bookingId}`,
      form: ProviderGroupPaperworkFormName.VERIFICATION_AND_REVIEW,
      pages: VERIFICATION_AND_REVIEW_PAGES,
      dependencyFn: ({ paperwork }) =>
        sectionHasAvailableFields(VERIFICATION_AND_REVIEW_PAGES, paperwork),
      startingProgress: 80,
    },
  ],
  [
    PGSectionNumber.Consent,
    {
      getRoute: (bookingId) => `${PaperworkSectionRoute.CONSENT}/${bookingId}`,
      dependencyFn: ({ location, paperwork }) => isConsentIncluded(location, paperwork),
      startingProgress: 90,
    },
  ],
  [
    PGSectionNumber.Finalize,
    {
      getRoute: (bookingId) => `${ProviderGroupPaperworkSectionRoute.FINALIZE}/${bookingId}`,
      startingProgress: 95,
    },
  ],
]);
